import React, { ReactNode, useState } from 'react';
import { View, useWindowDimensions, type TextStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import {
  FadeInDown,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  type SharedValue,
} from 'react-native-reanimated';
import { AnimatedText, AnimatedView } from './animated';
import { PressableScale } from './pressable-scale';
import { ProviderNotificationBell } from '@/components/provider-notification-bell';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/store/authStore';
import { isProviderUser } from '@/utils/user-role';
import { useThemedHex } from '@/hooks/use-theme-colors';

const MOBILE_BREAKPOINT = 768;
/** Duas linhas de `text-sm leading-5` — teto do subtítulo antes de colapsar. */
const SUBTITLE_MAX_HEIGHT = 40;
/** `font-bold tracking-tight` em estilo: className não vale junto de estilo animado no nativo. */
const TITLE_BASE_STYLE: TextStyle = {
  fontFamily: 'Poppins_700Bold',
  letterSpacing: -0.5,
};

interface PageHeaderProps {
  /** Main title of the page */
  title: string;
  /** Subtitle/description text */
  subtitle?: string;
  /** Quando informado, mostra um chevron de voltar ao lado do título (sub-páginas) */
  onBack?: () => void;
  /** Desabilita o botão voltar (ex.: durante submit) */
  backDisabled?: boolean;
  /** Optional button title */
  buttonTitle?: string;
  /** Button press handler */
  onButtonPress?: () => void;
  /** Optional button variant */
  buttonVariant?: 'default' | 'outline' | 'ghost' | 'link';
  /** testID repassado ao botão quando não se usa action */
  buttonTestID?: string;
  /** Ações extras (ex.: ícones); com `buttonTitle`, ficam antes do botão padrão */
  action?: ReactNode;
  /** Additional CSS classes for the container */
  className?: string;
  /**
   * Renderiza como faixa fixa acima do scroll (uso do `PageLayout`): ganha
   * padding de safe-area, borda inferior ao compactar e o sino de
   * notificações no mobile. Sem isto, mantém o comportamento antigo (linha
   * solta, sem faixa) para quem ainda usa `PageHeader` fora do `PageLayout`.
   */
  asStrip?: boolean;
  /** Progresso 0→1 do estado compacto da faixa, controlado pelo `PageLayout`. Só usado com `asStrip`. */
  compactProgress?: SharedValue<number>;
}

/**
 * Cabeçalho de página. Fora do `PageLayout` (uso legado), é uma linha solta
 * com fade de entrada. Dentro do `PageLayout` (`asStrip`), é a faixa fixa
 * acima do scroll: título/subtítulo encolhem em um passo quando a lista
 * rola, sem acompanhar o dedo continuamente.
 */
export function PageHeader({
  title,
  subtitle,
  onBack,
  backDisabled = false,
  buttonTitle,
  onButtonPress,
  buttonVariant = 'default',
  buttonTestID,
  action,
  className = '',
  asStrip = false,
  compactProgress,
}: PageHeaderProps) {
  const th = useThemedHex();
  const titleColor = th('#000000');
  const { width } = useWindowDimensions();
  const isMobile = width < MOBILE_BREAKPOINT;
  // x real da coluna do título: o subtítulo do mobile (linha própria) recua
  // exatamente até ali, sem somar larguras de botão à mão.
  const [titleX, setTitleX] = useState(0);

  const { user } = useAuthStore();
  const isProvider = isProviderUser(user);
  const showBell = asStrip && isMobile && isProvider;

  // Fallback local: quem usa PageHeader fora do PageLayout (sem `asStrip`) não
  // passa `compactProgress` — o valor fica parado em 0 e os estilos abaixo
  // resolvem para o estado "expandido", sem custo extra.
  const fallbackProgress = useSharedValue(0);
  const progress = compactProgress ?? fallbackProgress;

  // A cor vai no estilo animado, não no estático: no iPhone o Reanimated não
  // aplicava a troca do estático depois de montado, e o título ficava preto
  // quando o tema salvo (lido depois da abertura) era o escuro.
  const titleStyle = useAnimatedStyle(
    () => ({
      fontSize: interpolate(progress.value, [0, 1], [26, 17]),
      color: titleColor,
    }),
    [titleColor],
  );

  // Colapso do subtítulo: opacity + maxHeight fixo (não altura medida via
  // onLayout). O subtítulo é uma linha só, sempre a mesma métrica — medir
  // seria mais correto em teoria, mas adiciona um round-trip de layout para
  // resolver algo que uma altura fixa já resolve sem gambiarra.
  const subtitleStyle = useAnimatedStyle(() => ({
    opacity: interpolate(progress.value, [0, 1], [1, 0]),
    maxHeight: interpolate(progress.value, [0, 1], [SUBTITLE_MAX_HEIGHT, 0]),
    marginTop: interpolate(progress.value, [0, 1], [2, 0]),
  }));

  const actionStyle = useAnimatedStyle(() => ({
    transform: [{ scale: interpolate(progress.value, [0, 1], [1, 0.88]) }],
  }));

  // O inset de topo do mobile é aplicado uma vez no `(tabs)/_layout.tsx` — vale
  // também para as telas que não usam PageLayout. Somar aqui dobraria a folga.
  const stripStyle = useAnimatedStyle(() => ({
    paddingTop: interpolate(progress.value, [0, 1], [16, 10]),
    paddingBottom: interpolate(progress.value, [0, 1], [16, 10]),
  }));

  // Compacta: em vez de uma divisória, uma sombra mínima separa a faixa da
  // lista. A régua é 1px da cor do fundo (invisível) só para projetar a sombra.
  const shadowStyle = useAnimatedStyle(() => ({
    opacity: interpolate(progress.value, [0, 1], [0, 1]),
  }));

  const backButton = onBack ? (
    <PressableScale
      onPress={onBack}
      disabled={backDisabled}
      haptic
      accessibilityRole="button"
      accessibilityLabel="Voltar"
      className={`mr-3 h-10 w-10 shrink-0 items-center justify-center rounded-full bg-card ${
        backDisabled ? 'opacity-40' : ''
      }`}
      style={{ boxShadow: '0 1px 2px rgba(23, 17, 10, 0.06)' }}
      hitSlop={8}
    >
      <Ionicons className="text-neutral-900" name="arrow-back" size={20} />
    </PressableScale>
  ) : null;

  // Na faixa do mobile, o botão de texto vira círculo com "+": rótulo longo
  // ("Criar contrato") espremia a coluna do título e cortava o subtítulo.
  const compactButton = asStrip && isMobile;
  const buttonNode =
    buttonTitle && onButtonPress ? (
      compactButton ? (
        <Button
          size="icon"
          onPress={onButtonPress}
          variant={buttonVariant}
          testID={buttonTestID}
          accessibilityLabel={buttonTitle}
        >
          <Ionicons
            name="add"
            size={24}
            color={buttonVariant === 'default' ? '#FFFFFF' : th('#171717')}
          />
        </Button>
      ) : (
        <Button
          title={buttonTitle}
          onPress={onButtonPress}
          variant={buttonVariant}
          testID={buttonTestID}
        />
      )
    ) : null;
  // `action` (ícones extras) e o botão padrão podem vir juntos: ícones antes.
  const actionNode =
    action && buttonNode ? (
      <View className="flex-row items-center gap-2">
        {action}
        {buttonNode}
      </View>
    ) : (
      (action ?? buttonNode)
    );

  if (!asStrip) {
    return (
      <AnimatedView
        entering={FadeInDown.springify().damping(20).delay(50)}
        className={`mb-4 flex-row items-center justify-between ${className}`}
      >
        {backButton}
        <View className="mr-3 flex-1 gap-0.5">
          <ThemedText className="font-bold text-2xl tracking-tight md:text-3xl">{title}</ThemedText>
          {subtitle && (
            <ThemedText className="text-sm leading-5 text-neutral-500 md:text-base">
              {subtitle}
            </ThemedText>
          )}
        </View>
        {actionNode ? <View className="shrink-0">{actionNode}</View> : null}
      </AnimatedView>
    );
  }

  const subtitleNode = subtitle ? (
    <AnimatedView style={[{ overflow: 'hidden' }, subtitleStyle]}>
      <ThemedText className="text-sm leading-5 text-neutral-500" numberOfLines={2}>
        {subtitle}
      </ThemedText>
    </AnimatedView>
  ) : null;

  return (
    <View className={`bg-canvas ${className}`}>
      {/* Layout em View comum: no nativo, className numa AnimatedView que também
          recebe estilo animado é descartado — a faixa virava coluna e o
          título sumia. AnimatedView aqui só carrega o que anima. */}
      <AnimatedView style={stripStyle}>
        <View className="flex-row items-center justify-between px-5">
          {backButton}
          <View className="mr-3 flex-1" onLayout={(e) => setTitleX(e.nativeEvent.layout.x)}>
            <AnimatedText style={[TITLE_BASE_STYLE, titleStyle]} numberOfLines={1}>
              {title}
            </AnimatedText>
            {subtitle && !isMobile ? subtitleNode : null}
          </View>
          {showBell ? <ProviderNotificationBell className="mr-1" /> : null}
          {actionNode ? (
            <AnimatedView style={[{ flexShrink: 0 }, actionStyle]}>{actionNode}</AnimatedView>
          ) : null}
        </View>
        {/* Mobile: subtítulo em linha própria, alinhado ao início do título. */}
        {subtitle && isMobile ? (
          <View className="pr-5" style={{ paddingLeft: titleX || 20 }}>
            {subtitleNode}
          </View>
        ) : null}
      </AnimatedView>
      <AnimatedView
        style={[
          {
            height: 1,
            width: '100%',
            backgroundColor: th('#f5f5f5'),
            boxShadow: '0 3px 5px -2px rgba(23, 17, 10, 0.10)',
          },
          shadowStyle,
        ]}
      />
    </View>
  );
}
